import { logger } from "@/utils/logger";
import { useEffect, useRef, useCallback } from 'react';
import { FormFrameworkRef } from '@/components/FormFramework';

interface UseFormAutoCollapseOptions {
  enabled?: boolean;
  formRef: React.RefObject<FormFrameworkRef>;
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  debounceMs?: number;
  temporaryDisabled?: boolean;
}

export const useFormAutoCollapse = ({
  enabled = true,
  formRef,
  scrollAreaRef,
  debounceMs = 100,
  temporaryDisabled = false
}: UseFormAutoCollapseOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isCollapsedRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const lastActionTimeRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Detect Safari to disable auto-collapse (Safari has issues with DOM queries)
  const isSafari = useRef(false);
  
  useEffect(() => {
    isSafari.current = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  const checkScrollPosition = useCallback(() => {
    if (!enabled || temporaryDisabled || !scrollAreaRef.current || isSafari.current) return;
    
    try {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (!scrollElement) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 15;
      
      isAtBottomRef.current = isAtBottom;
      
      if (isAtBottom && isCollapsedRef.current) {
        formRef.current?.expand();
        isCollapsedRef.current = false;
      }
    } catch (error) {
      // Silently handle DOM query errors in Safari
    }
  }, [enabled, scrollAreaRef, formRef, temporaryDisabled]);

  const handleScrollActivity = useCallback(() => {
    if (!enabled || temporaryDisabled || !formRef.current || !scrollAreaRef.current || isSafari.current) return;
    
    // Check if any input/textarea/select currently has focus
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT'
    );
    
    // Check if any form fields have values
    const hasFilledFields = () => {
      const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea, select');
      return Array.from(inputs).some((input: any) => input.value && input.value.trim().length > 0);
    };
    
    // CRITICAL: Don't auto-collapse if user has focus in a field AND has filled at least one field
    if (isInputFocused && hasFilledFields()) {
      logger.log('🚫 Auto-collapse BLOCKED: User has focus and filled fields');
      return;
    }
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      try {
        const scrollElement = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        if (!scrollElement) return;
        
        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
        const isAwayFromBottom = scrollTop + clientHeight < scrollHeight - 100;
        const now = Date.now();
        
        isAtBottomRef.current = isAtBottom;
        
        // Double-check before collapsing
        const activeEl = document.activeElement;
        const inputFocused = activeEl && (
          activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.tagName === 'SELECT'
        );
        
        if (isAwayFromBottom && !isCollapsedRef.current && now - lastActionTimeRef.current > 1000 && !inputFocused) {
          formRef.current.collapse();
          isCollapsedRef.current = true;
          lastActionTimeRef.current = now;
        }
        
        if (isAtBottom && isCollapsedRef.current && now - lastActionTimeRef.current > 500) {
          formRef.current.expand();
          isCollapsedRef.current = false;
          lastActionTimeRef.current = now;
        }
      } catch (error) {
        // Silently handle DOM query errors in Safari
      }
    }, 300);
  }, [enabled, formRef, scrollAreaRef, temporaryDisabled]);

  const handleMouseEnter = useCallback(() => {
    if (!enabled || temporaryDisabled || isSafari.current) return;
    
    // Check if any input/textarea/select currently has focus
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT'
    );
    
    // Check if any form fields have values
    const hasFilledFields = () => {
      const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea, select');
      return Array.from(inputs).some((input: any) => input.value && input.value.trim().length > 0);
    };
    
    // CRITICAL: Don't trigger auto-collapse on mouse enter if user has focus and filled fields
    if (isInputFocused && hasFilledFields()) {
      logger.log('🚫 Mouse enter auto-collapse BLOCKED: User has focus and filled fields');
      return;
    }
    
    handleScrollActivity();
  }, [enabled, handleScrollActivity, temporaryDisabled]);

  useEffect(() => {
    if (!enabled || temporaryDisabled || !scrollAreaRef.current || isSafari.current) return;
    
    try {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (!scrollElement) return;

      scrollElement.addEventListener('scroll', handleScrollActivity, { passive: true });
      scrollAreaRef.current.addEventListener('mouseenter', handleMouseEnter, { passive: true });
      
      checkScrollPosition();
      
      return () => {
        scrollElement.removeEventListener('scroll', handleScrollActivity);
        scrollAreaRef.current?.removeEventListener('mouseenter', handleMouseEnter);
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } catch (error) {
      // Silently handle DOM query errors in Safari
    }
  }, [enabled, scrollAreaRef, handleScrollActivity, handleMouseEnter, checkScrollPosition, temporaryDisabled]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    isCollapsed: isCollapsedRef.current,
    isAtBottom: isAtBottomRef.current
  };
};